import { ArrowRightOutlined, BookOutlined, MessageOutlined, PictureOutlined } from '@ant-design/icons';
import WelcomeModalComponent, {
  WELCOME_MODAL_NAME
} from 'components/Account/WelcomeModalComponent/WelcomeModalComponent';
import { HIPAA_ACCEPT_MODAL_NAME } from 'components/Modals/HipaaAcceptModal/HipaaAcceptModal';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import Footer from 'pages/Client/Footer/Footer';
import Header from 'pages/Client/Header/Header';
import type { KeyboardEvent } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAuthUser } from 'store/authSlice';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { shouldRequireHipaaAcceptance } from 'utils/identityGuards';
import './Home.css';

function Home() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectAuthUser);
  const firstName = user?.firstName?.trim();
  const welcomeText = firstName ? `Welcome, ${firstName}` : 'Welcome';

  useEffect(() => {
    if (user && !user.isInitialized) {
      dispatch(openModal({ name: WELCOME_MODAL_NAME }));
    }
  }, [dispatch, user]);

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, route: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick(route);
    }
  };

  const handleCardClick = (route: string) => {
    if (shouldRequireHipaaAcceptance(user)) {
      dispatch(openModal({ name: HIPAA_ACCEPT_MODAL_NAME }));
      return;
    }

    navigate(route);
  };

  return (
    <>
      <div className="home-page layout-full-height">
        <Header />

        <ContainerComponent className="layout-main-content">
          <div className="home-page-content">
            <div className="home-page-grid">
              <div className="home-page-intro">
                <p className="home-page-welcome">{welcomeText}</p>
                <h1>What would you like to create?</h1>
                <p className="home-page-subtitle">Choose an option below to get started.</p>
              </div>

              <div className="home-page-cards">
                <div
                  className="home-page-card home-page-card-primary"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCardClick('/app/life-story')}
                  onKeyDown={(event) => handleCardKeyDown(event, '/app/life-story')}
                >
                  <div className="home-page-card-icon" aria-hidden="true">
                    <BookOutlined />
                  </div>

                  <div className="home-page-card-content">
                    <h2>Life Story</h2>
                    <p>Capture the stories, memories, and experiences that shaped your life.</p>
                  </div>

                  <div className="home-page-card-arrow" aria-hidden="true">
                    <ArrowRightOutlined />
                  </div>
                </div>

                <div
                  className="home-page-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCardClick('/app/artifacts')}
                  onKeyDown={(event) => handleCardKeyDown(event, '/app/artifacts')}
                >
                  <div className="home-page-card-icon" aria-hidden="true">
                    <PictureOutlined />
                  </div>

                  <div className="home-page-card-content">
                    <h2>Artifacts</h2>
                    <p>Preserve photos, stories, and audio memories in one collection for sharing.</p>
                  </div>

                  <div className="home-page-card-arrow" aria-hidden="true">
                    <ArrowRightOutlined />
                  </div>
                </div>

                <div
                  className="home-page-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCardClick('/message')}
                  onKeyDown={(event) => handleCardKeyDown(event, '/message')}
                >
                  <div className="home-page-card-icon" aria-hidden="true">
                    <MessageOutlined />
                  </div>

                  <div className="home-page-card-content">
                    <h2>Messages</h2>
                    <p>Create personal messages for loved ones to share now, later, or after you're gone.</p>
                  </div>

                  <div className="home-page-card-arrow" aria-hidden="true">
                    <ArrowRightOutlined />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContainerComponent>

        <Footer />
      </div>

      <WelcomeModalComponent />
    </>
  );
}

export default Home;
